import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const navigation = [
  { name: 'Kurumsal', href: '/kurumsal' },
  { name: 'Hizmetler', href: '/hizmetler' },
  { name: 'Filo & Teknoloji', href: '/filo-teknoloji' },
  { name: 'Güvenlik & Kalite', href: '/guvenlik-kalite' },
  { name: 'İletişim', href: '/iletisim' },
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  const showLight = isHome && !isScrolled && !isMobileMenuOpen;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled || !isHome
          ? 'bg-white/95 backdrop-blur-sm shadow-[0_1px_0_rgba(0,0,0,0.06)] py-3'
          : 'bg-transparent py-5'
      }`}
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="header-logo">
            <img
              src={showLight ? '/logo-white.png' : '/logo-color.png'}
              alt="Asküçükler Lojistik"
              className={`w-auto transition-all duration-300 ${
                isScrolled || !isHome
                  ? 'h-20 sm:h-20 lg:h-24'
                  : 'h-20 sm:h-24 lg:h-28'
              }`}
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-10" data-testid="desktop-nav">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`text-[0.9375rem] font-medium transition-colors duration-200 ${
                  showLight
                    ? isActive(item.href) ? 'text-white' : 'text-white/75 hover:text-white'
                    : isActive(item.href) ? 'text-[#0B3A82]' : 'text-[#64748B] hover:text-[#0B3A82]'
                }`}
                data-testid={`nav-link-${item.name.toLowerCase().replace(/\s+/g, '-').replace('&', '')}`}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:block">
            <Link to="/iletisim">
              <Button
                className="bg-[#FF6B2C] hover:bg-[#E55A1F] text-white font-semibold px-7 py-2.5 rounded-full shadow-lg shadow-orange-500/20 transition-all duration-300 hover:shadow-orange-500/30"
                data-testid="header-cta-button"
              >
                Ekibimizle Görüşün
              </Button>
            </Link>
          </div>

          <button
            className={`lg:hidden p-2 ${showLight ? 'text-white' : 'text-[#1A1A2E]'}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="lg:hidden bg-white border-t border-gray-100"
            data-testid="mobile-menu"
          >
            <nav className="max-w-7xl mx-auto px-6 py-6 space-y-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`block py-3 text-[0.9375rem] font-medium rounded-lg px-3 transition-colors ${
                    isActive(item.href)
                      ? 'text-[#0B3A82] bg-blue-50/60'
                      : 'text-[#1A1A2E] hover:bg-gray-50'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              <div className="pt-4 mt-4 border-t border-gray-100">
                <Link to="/iletisim" className="block">
                  <Button className="w-full bg-[#FF6B2C] hover:bg-[#E55A1F] text-white rounded-full font-semibold py-3">
                    Ekibimizle Görüşün
                  </Button>
                </Link>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
